import type {ReactElement} from 'react';
import React from 'react';
import {useAppSelector, useAppDispatch} from '@/hooks';
import {getCurrentPageDuration} from '@Components/poster-editor/poster-editor-reducer';
import {updateTimelineHoverPosition, clearSelectedTimelineItem} from './timeline-reducer';
import {isEditorMobileVariant} from '@Components/poster-editor/library/is-editor-mobile-variant';
import {useTimelineSeekToClientX} from './hooks/timeline-hooks';
import {useTimelineRefs} from './hooks/useTimelineRefs';
import {TimelineRefsProvider} from './timeline-context';
import {useTimelineWheelZoom} from './hooks/useTimelineWheelZoom';
import {useTimelineHeightObserver} from './hooks/useTimelineHeightObserver';
import {useClickOutsideToDeselect} from './hooks/useClickOutsideToDeselect';
import {TimelineHoverLine} from './components/timeline-hover-line/timeline-hover-line';
import {TimelineSeekIndicator} from './components/seek-indicator/seek-indicator';
import {TimelineRuler} from './components/timeline-ruler/timeline-ruler';
import {PageTimelineTrack} from './components/page-timeline-track/page-timeline-track';
import {AudioTimelineTrack} from './components/audio-timeline-track/audio-timeline-track';
import {TranscriptTimelineTrack} from './components/transcript-timeline-track/transcript-timeline-track';
import {TIMELINE_HORIZONTAL_PADDING} from './timeline.types';
import styles from './timeline.module.scss';

function TimelineContent(): ReactElement | null {
  const {scrollContainerRef} = useTimelineRefs();
  const dispatch = useAppDispatch();
  const currentPageDuration = useAppSelector(getCurrentPageDuration);
  const seekToClientX = useTimelineSeekToClientX();

  useTimelineWheelZoom(scrollContainerRef);
  useTimelineHeightObserver(scrollContainerRef);
  useClickOutsideToDeselect();

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!scrollContainerRef.current) {
      return;
    }
    const containerRect = scrollContainerRef.current.getBoundingClientRect();
    const scrollLeft = scrollContainerRef.current.scrollLeft;
    const pointerX = e.clientX - containerRect.left + scrollLeft - TIMELINE_HORIZONTAL_PADDING;
    const pointerPosition = Math.max(0, pointerX);
    dispatch(updateTimelineHoverPosition(pointerPosition));
  };

  const handleMouseLeave = (): void => {
    dispatch(updateTimelineHoverPosition(undefined));
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    dispatch(clearSelectedTimelineItem());
    if (!isEditorMobileVariant()) {
      seekToClientX(e.clientX);
    }
  };

  if (!currentPageDuration) {
    return null;
  }

  return (
    <div
      ref={scrollContainerRef}
      className={styles.scrollContainer}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      <div className={styles.scrollableContainer} style={{padding: `0 ${TIMELINE_HORIZONTAL_PADDING}px`}}>
        <TimelineSeekIndicator />
        <TimelineHoverLine />
        <TimelineRuler />
        <div className={styles.tracksContainer}>
          <PageTimelineTrack />
          <AudioTimelineTrack />
          <TranscriptTimelineTrack />
        </div>
      </div>
    </div>
  );
}

export function Timeline(): ReactElement | null {
  return (
    <TimelineRefsProvider>
      <TimelineContent />
    </TimelineRefsProvider>
  );
}
